"use client";

import { Container, Title, Text, SimpleGrid, Card, Badge, Group, Stack, Button, Box, Paper, ThemeIcon } from "@mantine/core";
import { IconArrowRight, IconSparkles } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { styleEssences, essenceIds } from "./essenceData";
import { useTranslations } from "next-intl";

// Accent colors matching the content pages
const essenceAccentColors: Record<string, string> = {
  dramatic: "violet",
  natural: "teal",
  gamine: "pink",
  classic: "blue",
  romantic: "rose",
  ingenue: "pink",
  ethereal: "indigo",
};

// Yin-Yang display helper
const yinYangLabels: Record<string, { label: string; color: string }> = {
  yang: { label: "Yang", color: "dark" },
  yin: { label: "Yin", color: "pink" },
  balanced: { label: "Balanced", color: "gray" },
};

export default function StyleEssenceContent({ locale }: { locale: string }) {
  const t = useTranslations("styleEssence.page");
  // Group essences by yin/yang
  const yangEssences = essenceIds.filter((id) => styleEssences[id].yinYang === "yang");
  const yinEssences = essenceIds.filter((id) => styleEssences[id].yinYang === "yin");
  const balancedEssences = essenceIds.filter((id) => styleEssences[id].yinYang === "balanced");

  return (
    <Container size="lg" py="xl">
      {/* Hero Section */}
      <Stack gap="md" ta="center" mb="xl">
        <Badge size="lg" variant="light" color="violet">
          {t("badge")}
        </Badge>
        <Title order={1} fz={{ base: 32, md: 42 }} fw={300}>
          {t("title")}
        </Title>
        <Text c="dimmed" maw={700} mx="auto" fz="lg">
          {t("subtitle")}
        </Text>
        <Button
          component={Link}
          href="/style-personality-quiz"
          size="lg"
          color="violet"
          rightSection={<IconArrowRight size={18} />}
        >
          {t("takeQuiz")}
        </Button>
      </Stack>

      {/* What is Style Essence */}
      <Paper shadow="xs" p="xl" radius="md" mb="xl" withBorder>
        <Title order={2} fz="xl" mb="md">
          {t("whatIsTitle")}
        </Title>
        <Text style={{ lineHeight: 1.8 }} mb="md">
          {t("whatIsP1")}
        </Text>
        <Text style={{ lineHeight: 1.8 }} mb="md" dangerouslySetInnerHTML={{ __html: t("whatIsP2") }} />
        <Text style={{ lineHeight: 1.8 }}>
          {t("whatIsP3")}
        </Text>
      </Paper>

      {/* Yin/Yang Explanation */}
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" mb="xl">
        <Paper shadow="xs" p="lg" radius="md" withBorder bg="dark.0">
          <Group gap="xs" mb="sm">
            <Badge color="dark" size="lg">{t("yangEssences")}</Badge>
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            {t("yangDescription")}
          </Text>
          <Stack gap="xs">
            {yangEssences.map((id) => (
              <Badge key={id} variant="outline" color={essenceAccentColors[id]}>
                {styleEssences[id].name}
              </Badge>
            ))}
          </Stack>
        </Paper>

        <Paper shadow="xs" p="lg" radius="md" withBorder bg="gray.0">
          <Group gap="xs" mb="sm">
            <Badge color="gray" size="lg">{t("balancedEssence")}</Badge>
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            {t("balancedDescription")}
          </Text>
          <Stack gap="xs">
            {balancedEssences.map((id) => (
              <Badge key={id} variant="outline" color={essenceAccentColors[id]}>
                {styleEssences[id].name}
              </Badge>
            ))}
          </Stack>
        </Paper>

        <Paper shadow="xs" p="lg" radius="md" withBorder bg="pink.0">
          <Group gap="xs" mb="sm">
            <Badge color="pink" size="lg">{t("yinEssences")}</Badge>
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            {t("yinDescription")}
          </Text>
          <Stack gap="xs">
            {yinEssences.map((id) => (
              <Badge key={id} variant="outline" color={essenceAccentColors[id]}>
                {styleEssences[id].name}
              </Badge>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* All 7 Essences Grid */}
      <Title order={2} fz="xl" mb="lg" ta="center">
        {t("exploreAllTitle")}
      </Title>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" mb="xl">
        {essenceIds.map((id) => {
          const essence = styleEssences[id];
          const color = essenceAccentColors[id] || "violet";
          const yinYang = yinYangLabels[essence.yinYang];

          return (
            <Card
              key={id}
              component={Link}
              href={`/style-essence/${id}`}
              withBorder
              padding="lg"
              radius="md"
              style={{
                textDecoration: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              className="hover-card"
            >
              <Stack gap="sm">
                <Group justify="space-between">
                  <Badge color={color} variant="light" size="md">
                    {essence.name}
                  </Badge>
                  <Badge color={yinYang.color} variant="outline" size="sm">
                    {essence.yinYangLevel}
                  </Badge>
                </Group>

                <Title order={3} fz="lg" fw={500}>
                  {essence.name} {t("essenceSuffix")}
                </Title>

                <Text fz="sm" c="dimmed" fs="italic" lineClamp={2}>
                  "{essence.tagline}"
                </Text>

                <Text fz="sm" lineClamp={3}>
                  {essence.description}
                </Text>

                {/* Energy words preview */}
                <Group gap={4} wrap="wrap">
                  {essence.energyWords.slice(0, 3).map((word, i) => (
                    <Badge key={i} variant="dot" color={color} size="xs">
                      {word}
                    </Badge>
                  ))}
                </Group>

                {/* Celebrity preview */}
                <Text fz="xs" c="dimmed">
                  {t("celebrities")}: {essence.celebrities.slice(0, 2).map((c) => c.name).join(", ")}
                </Text>
              </Stack>
            </Card>
          );
        })}
      </SimpleGrid>

      {/* How to Find Your Essence */}
      <Paper shadow="xs" p="xl" radius="md" mb="xl" withBorder>
        <Title order={2} fz="xl" mb="md">
          {t("howToFindTitle")}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Box>
            <Title order={3} fz="md" mb="sm">{t("facialFeaturesTitle")}</Title>
            <Text size="sm" c="dimmed" mb="md">
              {t("facialFeaturesIntro")}
            </Text>
            <Stack gap="xs">
              <Text size="sm">• {t("facialQ1")}</Text>
              <Text size="sm">• {t("facialQ2")}</Text>
              <Text size="sm">• {t("facialQ3")}</Text>
              <Text size="sm">• {t("facialQ4")}</Text>
            </Stack>
          </Box>
          <Box>
            <Title order={3} fz="md" mb="sm">{t("naturalEnergyTitle")}</Title>
            <Text size="sm" c="dimmed" mb="md">
              {t("naturalEnergyIntro")}
            </Text>
            <Stack gap="xs">
              <Text size="sm">• {t("energyQ1")}</Text>
              <Text size="sm">• {t("energyQ2")}</Text>
              <Text size="sm">• {t("energyQ3")}</Text>
              <Text size="sm">• {t("energyQ4")}</Text>
            </Stack>
          </Box>
        </SimpleGrid>
      </Paper>

      {/* Essence vs Kibbe vs Color */}
      <Paper shadow="xs" p="xl" radius="md" mb="xl" withBorder bg="violet.0">
        <Title order={2} fz="xl" mb="md">
          {t("vsOtherSystemsTitle")}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          <Box>
            <Title order={3} fz="md" mb="sm">{t("essenceSystemTitle")}</Title>
            <Text size="sm" c="dimmed" dangerouslySetInnerHTML={{ __html: t("essenceSystemDesc") }} />
          </Box>
          <Box>
            <Title order={3} fz="md" mb="sm">{t("kibbeSystemTitle")}</Title>
            <Text size="sm" c="dimmed" dangerouslySetInnerHTML={{ __html: t("kibbeSystemDesc") }} />
          </Box>
          <Box>
            <Title order={3} fz="md" mb="sm">{t("colorSystemTitle")}</Title>
            <Text size="sm" c="dimmed" dangerouslySetInnerHTML={{ __html: t("colorSystemDesc") }} />
          </Box>
        </SimpleGrid>
        <Text size="sm" mt="lg" ta="center" dangerouslySetInnerHTML={{ __html: t("systemsWorkTogether") }} />
      </Paper>

      {/* CTA Section */}
      <Box ta="center" mt="xl" pt="xl">
        <ThemeIcon size={60} radius="xl" color="violet" variant="light" mx="auto" mb="md">
          <IconSparkles size={30} />
        </ThemeIcon>
        <Title order={2} fz="xl" mb="sm">
          {t("ctaTitle")}
        </Title>
        <Text c="dimmed" mb="lg" maw={500} mx="auto">
          {t("ctaSubtitle")}
        </Text>
        <Group justify="center" gap="md">
          <Button
            component={Link}
            href="/style-personality-quiz"
            size="lg"
            color="violet"
            rightSection={<IconArrowRight size={18} />}
          >
            {t("takeStyleQuiz")}
          </Button>
          <Button
            component={Link}
            href="/kibbe-body-type-test"
            size="lg"
            variant="outline"
            color="violet"
          >
            {t("kibbeBodyTypeTest")}
          </Button>
        </Group>
      </Box>

      <style>{`
        .hover-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
        }
      `}</style>
    </Container>
  );
}
